import { Component } from '@angular/core';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { Account } from '@services/accounts.service';
import { AssetsDataService } from '@services/assets-data.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { DataPoint, DatapointsDataService } from '@services/datapoints-data.service';
import { Organisation } from '@services/organisation-data.service';
import { VirtualSensor, VirtualSensorsService } from '@services/virtual-sensors.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
	selector: 'app-virtual-sensor-details',
	templateUrl: './virtual-sensor-details.component.html',
	styleUrl: './virtual-sensor-details.component.scss'
})
export class VirtualSensorDetailsComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** AccountGUID */
	public AccountGUID: string;

	/** OrganisationUID */
	public OrganisationUID: string;

	/** currentUser */
	public currentUser: WebAppUser;

	/** account */
	public account: Account;

	/** organisation */
	public organisation: Organisation;

	/** virtualSensor */
	public virtualSensor: VirtualSensor;

	/** inputDataPoints */
	public inputDataPoints: DataPoint[] = [];

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService 
	 * @param globalFunctions globalFunctions 
	 * @param authenticationService authenticationService 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private virtualSensorsService: VirtualSensorsService,
		private datapointsDataService: DatapointsDataService
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, account, selectedOrganisationUID, selectedOrganisation, hierarchicalData, currentUser, params]) =>
				!!selectedAccountGUID && !!account && !!selectedOrganisationUID && !!selectedOrganisation && !!hierarchicalData && !!currentUser && !!params)
		).subscribe({
			next: ([selectedAccountGUID, account, selectedOrganisationUID, selectedOrganisation, hierarchicalData, currentUser, params]) => {
				this.AccountGUID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.account = account;
				this.organisation = selectedOrganisation;

				if (params.has("virtualSensorID")) {
					this.getVirtualSensors(parseInt(params.get("virtualSensorID") ?? ""));
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getVirtualSensors
	 * @param virtualSensorID virtualSensorID 
	 */
	getVirtualSensors(virtualSensorID: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.getVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensorID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((virtualSensors) => !!virtualSensors),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (virtualSensors) => {
				this.virtualSensor = virtualSensors[0];
				const inputDPs = JSON.parse(this.virtualSensor.InputDataPoints);
				let idString: string = "";
				Object.entries(inputDPs).forEach((item) => idString += `${item[1]},`);
				idString = idString.slice(0, idString.length - 1);
				this.GetDataPointsFromID(this.AccountGUID, this.OrganisationUID, idString);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.getVirtualSensor$", false);
			},
		});
	}

	/**
	 * GetDataPointsFromID
	 * @param accountID accountID 
	 * @param orgUID orgUID 
	 */
	async GetDataPointsFromID(accountID: string, orgUID: string, ids: string): Promise<DataPoint[]> {

		return new Promise((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.datapointsDataService.getDataPoints$(accountID, orgUID, ids).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((dataPoints) => !!dataPoints),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (dataPoints: any) => {
					this.inputDataPoints = dataPoints;
					resolve(dataPoints);
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getDataPoints$", false);
					}
					reject();
				},
				//complete: () => { console.log("this.AssetsDataService.getDataPoints$.subscribe: Complete"); }
			});
		});
	}
}
